import { type ReactNode, useId } from 'react';
import { CaretRightIcon, SparkleIcon } from '@phosphor-icons/react';

import { InputField } from '@/components/ui/Input';
import { roundToPence } from 'src/features/pam/costFormatting';

interface ActionCostSectionProps {
  revenueCost: string;
  capitalCost: string;
  onRevenueChange: (value: string) => void;
  onCapitalChange: (value: string) => void;
  errors?: { revenue_cost?: string; capital_cost?: string };
  isEditable: boolean;
  isLoading?: boolean;
  onEstimate?: () => void;
  isEstimating?: boolean;
  estimateSection?: ReactNode;
  /**
   * The action's stored total, used only as the fallback when neither component
   * is set (legacy cost-only actions) so their historic total stays visible.
   */
  storedTotal?: number | null;
}

function parseAmount(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed.replaceAll(',', ''));
  return Number.isFinite(parsed) ? parsed : null;
}

function formatGbp(amount: number): string {
  return `£${amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function normaliseCurrencyInput(value: string): string {
  const amount = parseAmount(value);
  return amount === null
    ? value
    : amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Cost breakdown for an action. Total cost is derived from the revenue and
 * capital components; when neither is set it falls back to the stored
 * (possibly legacy, unclassified) total so historic values stay visible.
 */
export function ActionCostSection({
  revenueCost,
  capitalCost,
  onRevenueChange,
  onCapitalChange,
  errors,
  isEditable,
  isLoading = false,
  onEstimate,
  isEstimating = false,
  estimateSection,
  storedTotal = null
}: ActionCostSectionProps) {
  const revenueFieldId = useId();
  const capitalFieldId = useId();
  const revenue = parseAmount(revenueCost);
  const capital = parseAmount(capitalCost);
  // The total follows the split when either component is set; otherwise it falls
  // back to the action's stored total (legacy, unclassified) so it stays visible.
  // Each component is rounded to pence first — as the backend quantises before
  // summing — so the total can't disagree with the persisted value when a user
  // types more than two decimal places.
  const totalCost =
    revenue !== null || capital !== null ? roundToPence(revenue ?? 0) + roundToPence(capital ?? 0) : storedTotal;

  return (
    <section>
      <div className="">
        <p className="text-xs font-medium text-content-subtle">Costs (optional)</p>
        <div>
          <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)] gap-x-3 gap-y-1.5">
            <div className="flex items-center">
              <label htmlFor={isEditable ? revenueFieldId : undefined} className="block text-xs text-content-subtle">
                Revenue (£)
              </label>
            </div>
            <div>
              {isEditable ? (
                <InputField
                  id={revenueFieldId}
                  type="text"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={revenueCost}
                  onChange={(e) => onRevenueChange(e.target.value)}
                  onBlur={(e) => onRevenueChange(normaliseCurrencyInput(e.target.value))}
                  disabled={isLoading}
                  placeholder="e.g. 1500.00"
                  fullWidth
                  prefix="£"
                  inputClassName="text-right lining-nums tabular-nums"
                  wrapperClassName={`!h-8 !px-2 text-xs ${errors?.revenue_cost ? '!border-red-500' : ''}`}
                />
              ) : (
                <p id={revenueFieldId} className="p-1 text-xs text-content-subtle">
                  {revenue === null ? <span className="text-content-subtle">Not set</span> : formatGbp(revenue)}
                </p>
              )}
              {errors?.revenue_cost && <p className="text-red-500 text-sm mt-1">{errors.revenue_cost}</p>}
            </div>

            <div className="flex items-center">
              <label htmlFor={isEditable ? capitalFieldId : undefined} className="block text-xs text-content-subtle">
                Capital (£)
              </label>
            </div>
            <div>
              {isEditable ? (
                <InputField
                  id={capitalFieldId}
                  type="text"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={capitalCost}
                  onChange={(e) => onCapitalChange(e.target.value)}
                  onBlur={(e) => onCapitalChange(normaliseCurrencyInput(e.target.value))}
                  disabled={isLoading}
                  placeholder="e.g. 1500.00"
                  fullWidth
                  prefix="£"
                  inputClassName="text-right lining-nums tabular-nums"
                  wrapperClassName={`!h-8 !px-2 text-xs ${errors?.capital_cost ? '!border-red-500' : ''}`}
                />
              ) : (
                <p id={capitalFieldId} className="p-1 text-xs text-content-subtle">
                  {capital === null ? <span className="text-content-subtle">Not set</span> : formatGbp(capital)}
                </p>
              )}
              {errors?.capital_cost && <p className="text-red-500 text-sm mt-1">{errors.capital_cost}</p>}
            </div>
            <span className="flex items-center text-xs text-content-subtle">Total (£)</span>
            <p className="p-1 text-xs text-content-subtle lining-nums tabular-nums">
              {totalCost === null ? <span className="text-content-subtle">Not set</span> : formatGbp(totalCost)}
            </p>
          </div>
        </div>
        {isEditable && onEstimate && (
          <div>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-misc-link hover:underline disabled:cursor-not-allowed disabled:opacity-60"
              disabled={isLoading || isEstimating}
              onClick={onEstimate}
            >
              <SparkleIcon size={16} weight="fill" aria-hidden />
              {isEstimating ? 'Generating estimate…' : 'Get AI cost estimate'}
              <CaretRightIcon size={14} weight="bold" aria-hidden />
            </button>
          </div>
        )}
      </div>
      {estimateSection}
    </section>
  );
}
